import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';

interface Props {
  onClose: () => void;
  deploymentId: string;
  workerName: string;
  projectName: string;
}

export function DnaRecordModal({ onClose, deploymentId, workerName, projectName }: Props) {
  const qc = useQueryClient();
  const [recordedBy, setRecordedBy] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const save = useMutation({
    mutationFn: async () => api.post('/deployments/mark-dna', { deploymentId, recordedBy, notes: notes.trim() || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deployments'] });
      toast.success('DNA recorded', workerName);
      onClose();
    },
    onError: () => toast.error('Save failed'),
  });

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-hdr" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div className="modal-title">⚠ Record DNA — Did Not Arrive</div>
          <button className="modal-close close-btn" onClick={onClose}>✕</button>
        </div>
        <div style={{ background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.2)', borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#ef4444', marginBottom: 2 }}>Worker did not arrive</div>
          <div id="dna-worker-label" style={{ fontSize: 12, color: 'var(--text)' }}>{workerName} → {projectName}</div>
        </div>
        <div style={{ marginBottom: 10 }}>
          <label className="form-label">Recorded By <span style={{ color: '#ef4444' }}>*</span></label>
          <input className="form-input" id="dna-recorded-by" placeholder="Name of person recording this DNA" value={recordedBy} onChange={(e) => setRecordedBy(e.target.value)} style={{ width: '100%' }} />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label className="form-label">Notes (optional)</label>
          <textarea className="form-textarea" id="dna-notes" placeholder="Reason, contact attempt, etc…" value={notes} onChange={(e) => setNotes(e.target.value)} style={{ minHeight: 70, width: '100%' }} />
        </div>
        <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 12 }}>
          This will be recorded in the worker's profile, the DNA log, and the compliance tab.
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center', background: '#ef4444', borderColor: '#ef4444' }} onClick={() => save.mutate()} disabled={save.isPending || !recordedBy.trim()}>
            {save.isPending ? 'Recording…' : '⚠ Record DNA'}
          </button>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
